/**
 * Low-level 5-field cron parsing and field matching.
 *
 * Extracted verbatim from `cronCalendar.ts`, which had kept these private.
 * Two surfaces now need them: the campaign scheduling calendar (via
 * `cronCalendar`) and the pipeline Schedules page's next-run predictor (via
 * `cronNextRun`). Sharing one implementation is deliberate — two independent
 * cron engines would let those surfaces disagree about when a schedule fires,
 * and the divergence would stay invisible until someone compared them.
 *
 * This module intentionally has no imports: no date-fns, and none of the
 * campaign `Schedule` types that `cronCalendar` carries. That keeps the
 * pipeline code from dragging the campaign scheduling types in behind it.
 */

/**
 * A 5-field cron expression split into its parts:
 * `minute hour dayOfMonth month dayOfWeek`
 */
export interface CronParts {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

/** Field bounds, by cron position. Minute and hour are 0-based; the rest are 1-based. */
export const FIELD_RANGE = {
  minute: { min: 0, max: 59 },
  hour: { min: 0, max: 23 },
  dayOfMonth: { min: 1, max: 31 },
  month: { min: 1, max: 12 },
  dayOfWeek: { min: 0, max: 6 }
} as const;

/**
 * Split a cron expression into its five fields.
 * Returns null for anything that isn't exactly 5 whitespace-separated fields —
 * which covers 6-field (seconds) crons and `@daily`-style macros.
 */
export function parseCron(cron: string): CronParts | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const [minute = '', hour = '', dayOfMonth = '', month = '', dayOfWeek = ''] = parts;
  return { minute, hour, dayOfMonth, month, dayOfWeek };
}

/**
 * Check if a cron field matches a given value.
 * Handles: wildcard (*), specific value, ranges (1-5), lists (1,3,5), steps (star/6)
 *
 * `fieldMin` is the field's own lower bound, and it matters only for the
 * star/n form. Cron counts steps from the start of the field's range, not from
 * zero — so a star/2 day-of-month means the 1st, 3rd, 5th, while a star/2 hour
 * means 0, 2, 4. The two look alike until you notice day-of-month starts at 1.
 * Passing the wrong bound silently shifts every matching day by one.
 */
export function fieldMatches(field: string, value: number, fieldMin = 0): boolean {
  // Wildcard matches everything
  if (field === '*') return true;

  // Step pattern: */n or n/m
  if (field.includes('/')) {
    const [range = '', stepStr = ''] = field.split('/');
    const step = parseInt(stepStr, 10);
    if (isNaN(step) || step <= 0) return false;

    if (range === '*') {
      return value >= fieldMin && (value - fieldMin) % step === 0;
    }
    // Handle range/step like 0-23/2
    if (range.includes('-')) {
      const [start = NaN, end = NaN] = range.split('-').map(Number);
      if (value < start || value > end) return false;
      return (value - start) % step === 0;
    }
    const start = parseInt(range, 10);
    if (value < start) return false;
    return (value - start) % step === 0;
  }

  // Range pattern: n-m
  if (field.includes('-') && !field.includes(',')) {
    const [start = NaN, end = NaN] = field.split('-').map(Number);
    return value >= start && value <= end;
  }

  // List pattern: n,m,o
  if (field.includes(',')) {
    const values = field.split(',');
    return values.some(v => {
      // Handle range within list: 1,3-5,7
      if (v.includes('-')) {
        const [start = NaN, end = NaN] = v.split('-').map(Number);
        return value >= start && value <= end;
      }
      return parseInt(v, 10) === value;
    });
  }

  // Specific value
  return parseInt(field, 10) === value;
}

/**
 * Check if a cron expression runs on a specific day.
 *
 * Reads calendar fields off `date` using its *host-local* getters, so callers
 * working in another timezone must hand in a proxy Date built from that zone's
 * year/month/day rather than a raw instant.
 */
export function cronRunsOnDay(cron: CronParts, date: Date): boolean {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  const dayOfMonth = date.getDate();
  const month = date.getMonth() + 1; // 1-12

  // Check month
  if (!fieldMatches(cron.month, month, FIELD_RANGE.month.min)) {
    return false;
  }

  // For day matching, cron has special behavior:
  // If both dayOfMonth and dayOfWeek are specified (not *), it's OR logic
  // If only one is specified, it must match
  const domSpecified = cron.dayOfMonth !== '*';
  const dowSpecified = cron.dayOfWeek !== '*';

  if (domSpecified && dowSpecified) {
    // OR logic: either day of month or day of week matches
    return (
      fieldMatches(cron.dayOfMonth, dayOfMonth, FIELD_RANGE.dayOfMonth.min) ||
      fieldMatches(cron.dayOfWeek, dayOfWeek, FIELD_RANGE.dayOfWeek.min)
    );
  }

  if (domSpecified) {
    return fieldMatches(cron.dayOfMonth, dayOfMonth, FIELD_RANGE.dayOfMonth.min);
  }

  if (dowSpecified) {
    return fieldMatches(cron.dayOfWeek, dayOfWeek, FIELD_RANGE.dayOfWeek.min);
  }

  // Both are wildcards - runs every day
  return true;
}

/**
 * Every value in `[min, max]` the field matches, ascending.
 *
 * The primitive next-run prediction needs: given a star/15 step for the minute
 * field, enumerate {0,15,30,45} so the iterator can step through candidate
 * times instead of scanning all 1440 minutes of a day.
 *
 * An empty result means the field is unsatisfiable (e.g. `99`), which callers
 * should treat the same as an unparseable expression.
 */
export function matchingValues(field: string, min: number, max: number): number[] {
  const out: number[] = [];
  for (let v = min; v <= max; v++) {
    if (fieldMatches(field, v, min)) out.push(v);
  }
  return out;
}
